// Shared language preference cookie, readable by every *.stitchpress.kinnet.studio
// site (landing, editor, help). Contract lives in the landing repo:
// docs/superpowers/specs/2026-07-23-cross-site-language-sync-design.md — the
// landing and help-site repos carry an equivalent copy of this helper.

export const LANG_COOKIE = 'stitchpress-lang';
const COOKIE_DOMAIN = 'stitchpress.kinnet.studio';

function onCookieDomain(hostname: string): boolean {
    return hostname === COOKIE_DOMAIN || hostname.endsWith(`.${COOKIE_DOMAIN}`);
}

function cookieValue(cookieHeader: string, name: string): string | null {
    for (const part of cookieHeader.split(';')) {
        const eq = part.indexOf('=');
        if (eq === -1) continue;
        if (part.slice(0, eq).trim() !== name) continue;
        try {
            const value = decodeURIComponent(part.slice(eq + 1).trim());
            return value || null;
        } catch {
            return null; // malformed percent-encoding — treat as absent
        }
    }
    return null;
}

/** Value of the shared language cookie, or null when absent / off-domain (localhost). */
export function readLangCookie(cookieHeader: string, hostname: string): string | null {
    if (!onCookieDomain(hostname)) return null;
    return cookieValue(cookieHeader, LANG_COOKIE);
}

/** Serialized Set-Cookie string per the shared contract, or null off-domain. */
export function langCookieString(lang: string, hostname: string): string | null {
    if (!onCookieDomain(hostname)) return null;
    return `${LANG_COOKIE}=${encodeURIComponent(lang)}; Domain=.${COOKIE_DOMAIN}; Path=/; Max-Age=31536000; SameSite=Lax; Secure`;
}

/** Persist an explicit language pick for all stitchpress sites. No-op off-domain. */
export function writeLangCookie(lang: string): void {
    const cookie = langCookieString(lang, window.location.hostname);
    if (cookie) document.cookie = cookie;
}

/**
 * Heuristic sign-in probe usable before Clerk has loaded: Clerk maintains a
 * `__client_uat` cookie (sometimes suffixed, e.g. `__client_uat_a1b2`) whose
 * value is a unix timestamp — `0` or absent means signed out. If this ever
 * misfires the cost is benign: the shared language cookie is applied once to a
 * signed-in user, and their next explicit pick re-persists their choice.
 */
export function isLikelySignedIn(cookieHeader: string): boolean {
    return cookieHeader.split(';').some((part) => {
        const eq = part.indexOf('=');
        if (eq === -1) return false;
        const name = part.slice(0, eq).trim();
        if (name !== '__client_uat' && !name.startsWith('__client_uat_')) return false;
        const value = part.slice(eq + 1).trim();
        return value !== '' && value !== '0';
    });
}
